import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { requests } from '../../core/config/config';
import { getAllowedRoutesForRole } from '../../core/config/menu-config';
import { ApiService } from '../../core/services/api/api-service';
import { AuthService } from '../../core/services/auth/auth-service';
import { LayoutService } from '../../core/services/layout.service';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './auth-callback.html',
  styleUrls: ['./auth-callback.scss']
})
export class AuthCallback implements OnInit {
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private authService: AuthService,
    private layoutService: LayoutService
  ) { }

  ngOnInit(): void {
    const rawCode = this.route.snapshot.queryParamMap.get('code') || '';
    const code = this.sanitizeCode(rawCode);

    if (!code) {
      this.failAndRedirectToLogin('Missing authorization code in callback URL.');
      return;
    }

    this.apiService.sendRequest(requests.azureCallback, 'get', { code }).subscribe({
      next: (res: any) => {
        const data = res?.data;
        if (!data?.token) {
          this.failAndRedirectToLogin(res?.message || 'Azure authentication failed.');
          return;
        }

        this.authService.clearAzureToken();
        this.authService.setAuthToken(data.token);
        this.authService.setUser(data);

        const firstRole = data.roles?.[0]?.id ?? null;
        const firstRegion = data.regions?.[0]?.id ?? data.regions_with_countries?.[0]?.id ?? null;
        const firstRegionCountries = (data.regions_with_countries || [])
          .find((region: any) => String(region?.id) === String(firstRegion))
          ?.countries || data.countries || [];
        const firstCountry = firstRegionCountries.find((country: any) => country?.id != null)?.id ?? null;
        this.layoutService.setSelectedRole(firstRole);
        this.layoutService.setSelectedRegion(firstRegion)
        this.layoutService.setSelectedCountry(firstCountry)

        // Get allowed routes for this role and navigate to the first one
        const allowedRoutes = getAllowedRoutesForRole(firstRole);
        const targetRoute = allowedRoutes.length > 0 ? allowedRoutes[0] : '/dashboard';
        this.router.navigate([targetRoute]);
      },
      error: (err: any) => {
        this.failAndRedirectToLogin(err?.error?.message || 'Unable to complete Azure sign-in.');
      }
    });
  }

  private sanitizeCode(code: string): string {
    return decodeURIComponent(code).replace(/^"+|"+$/g, '').trim();
  }

  private failAndRedirectToLogin(message: string): void {
    this.isLoading.set(false);
    this.errorMessage.set(message);
    this.authService.clearAzureToken();
    setTimeout(() => this.router.navigate(['/login']), 2000);
  }
}
